import React, { FC, useState } from 'react';
import { Input, Button, Card, Typography, message, Space } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Form } from '~/components';
import { passwordMinLength, useDispatchResetReduxState } from '../../login';
import { useLogin } from '~/hooks/auth';
import { useInvitationPageState } from '~/hooks/invitation';
import { useIdentity } from '~/hooks/identity';
import { auth } from '~/api/axios';

const { Title, Text } = Typography;

const ConfirmPassword: FC = () => {
  const apiIdentityQuery = useIdentity();
  const [pageState] = useInvitationPageState();
  const identityEmail = apiIdentityQuery.data?.username;
  const invitationEmail = pageState.data?.emailAddress;

  const [isEnterPassword, setEnterPassword] = useState(false);
  const handlEnterPassword = () => {
    setEnterPassword(true);
  };
  const resetState = useDispatchResetReduxState();
  const handleCancel = () => {
    auth.deleteToken();
    resetState();
  };

  const [isLoading, setLoading] = useState(false);

  const loginController = useLogin();

  const onFinish = async (values: any) => {
    const { password } = values;
    setLoading(true);
    if (password) {
      try {
        if (identityEmail) {
          try {
            await apiIdentityQuery.refetch();
            await loginController.login(identityEmail, password);
          } catch (error: any) {
            void message.error('Failed to login!', 10);
          }
        }
      } catch (e) {
        void message.error('Failed to set password!', 10);
      }
    }
    setLoading(false);
  };

  const { t } = useTranslation('invitation');

  const [form] = Form.useForm();

  return (
    <Card className="Invitation-Card">
      <Title className="Invitation-Title" level={2}>
        {t('translation:businessPortal').toUpperCase()}
      </Title>
      {isEnterPassword ? (
        <>
          <Form form={form} layout="vertical" size="large" onFinish={onFinish}>
            <Form.Item name="email" initialValue={identityEmail}>
              <Input placeholder={t('translation:email')} prefix={<UserOutlined />} disabled />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  min: passwordMinLength,
                },
              ]}
            >
              <Input.Password placeholder={t('translation:password')} prefix={<LockOutlined />} />
            </Form.Item>
            <Space direction="vertical" style={fullWidth}>
              <Form.Item noStyle>
                <MultilineButton type="primary" htmlType="submit" loading={isLoading}>
                  {t('translation:login')}
                </MultilineButton>
              </Form.Item>
              <MultilineButton onClick={handleCancel} size="large">
                <Trans
                  ns="invitation"
                  defaults="Enter another phone number and use <strong>{{invitationEmail}}</strong>"
                  values={{
                    invitationEmail,
                  }}
                />
              </MultilineButton>
            </Space>
          </Form>
        </>
      ) : (
        <>
          <Text type="secondary" className="Invitation-TextSignIn">
            <Trans
              ns="invitation"
              defaults="The phone number you have entered is already linked with the profile <strong>{{identityEmail}}</strong>. Do you want to log in as <strong>{{identityEmail}}</strong> or <strong>{{invitationEmail}}</strong>?"
              values={{
                identityEmail,
                invitationEmail,
              }}
            />
          </Text>
          <Space direction="vertical" style={fullWidth}>
            <MultilineButton onClick={handlEnterPassword} type="primary" size="large">
              <Trans
                ns="invitation"
                defaults="Yes, I want to continue as <strong>{{identityEmail}}</strong>"
                values={{
                  identityEmail,
                }}
              />
            </MultilineButton>
            <MultilineButton onClick={handleCancel} size="large">
              <Trans
                ns="invitation"
                defaults="No, I will enter another phone number and use <strong>{{invitationEmail}}</strong>"
                values={{
                  invitationEmail,
                }}
              />
            </MultilineButton>
          </Space>
        </>
      )}
    </Card>
  );
};

const MultilineButton = styled(Button)`
  width: 100%;
  white-space: break-spaces;
  height: auto;
`;

const fullWidth = { width: '100%' };

export default ConfirmPassword;
